import React, { useState } from 'react';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import {
  GeckoForm,
  GeckoFormField,
  GeckoModal,
  GeckoModalService,
  GeckoToastService,
} from 'ui';
import ChannelSelect from 'components/ChannelSelect';
import TagSelect from './TagSelect';
import moment from 'moment';

function startExportAction(
  dataType,
  setStartedExecution,
  startedExecution,
  exportStatus,
  dateRange,
  channels,
  tags
) {
  return GeckoRequest.post(`/exports/system/${dataType}`, {
    exportStatus,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
    channels,
    tags,
  })
    .then((_) =>
      setStartedExecution(
        Object.assign({}, startedExecution, { [dataType]: true })
      )
    )
    .then(() => {
      new GeckoToastService().success(
        `Exporting can sometimes take up to 30 minutes at peak times. We’ll email you a download link once the export is ready.`
      );
    })
    .catch(renderError);
}

const ExportModal = ({
  userEmail,
  dataType,
  setStartedExecution,
  startedExecution,
}) => {
  return (
    <GeckoForm
      initialValues={{
        exportStatus: 'all',
        dateRange: { from: undefined, to: undefined },
        channels: [],
        tags: [],
      }}
      onSubmit={(values) => {
        const formattedDateRange = {
          from:
            values.dateRange.from &&
            moment(values.dateRange.from).startOf('day').toDate(),
          to:
            values.dateRange.to &&
            moment(values.dateRange.to).endOf('day').toDate(),
        };
        startExportAction(
          dataType,
          setStartedExecution,
          startedExecution,
          values.exportStatus,
          formattedDateRange,
          values.channels,
          values.tags
        );
      }}
    >
      {({ handleSubmit, values, handleChange }) => (
        <GeckoModal
          name={`Confirm Export`}
          children={
            <div>
              <p>The export will be delivered to {userEmail}.</p>
              <p>Please confirm your export to proceed.</p>
              {dataType.toLowerCase() === 'conversation' && (
                <>
                  <GeckoFormField
                    label="Which conversation status would you like to export?"
                    fieldType={'select'}
                    name="exportStatus"
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'closed', label: 'Closed' },
                      { value: 'open', label: 'Open' },
                    ]}
                    containerStyle={{ padding: 0 }}
                  />
                  <GeckoFormField
                    label="Select date range"
                    description="Leave blank for the full range"
                    fieldType={'dateRange'}
                    name="dateRange"
                    containerStyle={{ padding: 0 }}
                  />
                  <GeckoFormField
                    label="Select Channels"
                    description="Leave blank for all channels"
                    containerStyle={{ padding: 0 }}
                    renderCustomComponent={() => (
                      <ChannelSelect
                        includeDeletedChannels={true}
                        value={values.channels}
                        multi={true}
                        onChange={(channels) =>
                          handleChange({
                            target: { name: 'channels', value: channels },
                          })
                        }
                      />
                    )}
                  />
                  <GeckoFormField
                    label="Select Tags"
                    description="Leave blank for all tags"
                    containerStyle={{ padding: 0, marginBottom: 0 }}
                    renderCustomComponent={() => (
                      <TagSelect
                        multi={true}
                        value={values.tags}
                        onChange={(tags) =>
                          handleChange({
                            target: { name: 'tags', value: tags },
                          })
                        }
                      />
                    )}
                  />
                </>
              )}
            </div>
          }
          btns={[
            {
              name: 'Cancel',
              secondary: true,
              icon: 'far fa-times',
              handler: () => {
                return new GeckoModalService().close();
              },
            },
            {
              name: 'Confirm Export',
              icon: 'far fa-download',
              handler: () => {
                handleSubmit();
                return new GeckoModalService().close();
              },
              type: 'primary',
            },
          ]}
        />
      )}
    </GeckoForm>
  );
};

export function openExportConfirmationModal(
  userEmail,
  setStartedExecution,
  dataType,
  startedExecution
) {
  return () => {
    new GeckoModalService().open(() => {
      return (
        <ExportModal
          {...{ userEmail, setStartedExecution, dataType, startedExecution }}
        />
      );
    });
  };
}
